import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  MagnifyingGlass,
  ShieldWarning,
  ShieldCheck,
  CircleNotch,
  ArrowRight,
  Warning,
  CaretLeft,
  CaretRight,
  UserCircle,
  Buildings,
  Info,
} from "@phosphor-icons/react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = API_URL || "";
const PAGE_SIZE = 20;

const OigExclusionLookup = () => {
  const { npiParam } = useParams();
  const navigate = useNavigate();

  const urlMode = npiParam ? "npi" : null;
  const [mode, setMode] = useState(urlMode || "npi");
  const [npiInput, setNpiInput] = useState(npiParam || "");
  const [lastName, setLastName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [stateFilter, setStateFilter] = useState("");

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  // Load stats on mount
  useEffect(() => {
    fetch(`${API_URL}/api/oig/stats`)
      .then(r => r.json())
      .then(setStats)
      .catch(() => {});
  }, []);

  // Auto-search from URL
  useEffect(() => {
    if (npiParam && npiParam.length === 10) {
      searchByNpi(npiParam);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npiParam]);

  const searchByNpi = async (npi) => {
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const res = await fetch(`${API_URL}/api/oig/search?npi=${npi}&limit=${PAGE_SIZE}`);
      const data = await res.json();
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
      setLastUpdated(data.last_updated);
    } catch {
      setError("Failed to search OIG database.");
    } finally {
      setLoading(false);
    }
  };

  const searchByName = async (pg = 1) => {
    setLoading(true);
    setError(null);
    if (pg === 1) setResults(null);
    setPage(pg);

    const params = new URLSearchParams();
    if (lastName.trim()) params.set("last_name", lastName.trim());
    if (firstName.trim()) params.set("first_name", firstName.trim());
    if (businessName.trim()) params.set("business_name", businessName.trim());
    if (stateFilter.trim()) params.set("state", stateFilter.trim());
    params.set("limit", PAGE_SIZE);
    params.set("skip", (pg - 1) * PAGE_SIZE);

    try {
      const res = await fetch(`${API_URL}/api/oig/search?${params.toString()}`);
      const data = await res.json();
      setResults(data.results || []);
      setTotalCount(data.result_count || 0);
      setHasMore(data.has_more || false);
      setLastUpdated(data.last_updated);
    } catch {
      setError("Failed to search OIG database.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    setError(null);
    if (mode === "npi") {
      if (!npiInput || npiInput.length !== 10) {
        setError("Please enter a valid 10-digit NPI number.");
        return;
      }
      navigate(`/oig-exclusion-lookup/npi/${npiInput}`);
    } else {
      if (!lastName.trim() && !businessName.trim()) {
        setError("Please enter a last name or business name.");
        return;
      }
      searchByName(1);
    }
  };

  const handlePageChange = (newPage) => {
    searchByName(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isLanding = !npiParam && !results;

  const buildTitle = () => {
    if (npiParam) return `OIG Exclusion NPI Lookup — NPI Number ${npiParam} Status`;
    return "OIG NPI Lookup — Free LEIE Exclusion & Sanctions Check | NPI Lookup";
  };

  return (
    <>
      <Helmet>
        <title>{buildTitle()}</title>
        <meta
          name="description"
          content="Free OIG NPI lookup. Check the HHS OIG LEIE exclusion database by NPI number, provider name, or organization. Verify federal healthcare sanctions instantly."
        />
        <link rel="canonical" href={`${SITE_URL}/oig-exclusion-lookup${npiParam ? `/npi/${npiParam}` : ""}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "OIG Exclusion Lookup",
          "description": "Check if a healthcare provider is excluded from federal healthcare programs using the HHS OIG LEIE database",
          "url": `${SITE_URL}/oig-exclusion-lookup`,
          "applicationCategory": "HealthApplication",
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Dataset",
          "name": "OIG LEIE Exclusion Database",
          "description": "List of Excluded Individuals/Entities from the HHS Office of Inspector General. 82,000+ records of providers excluded from federal healthcare programs.",
          "url": `${SITE_URL}/oig-exclusion-lookup`,
          "license": "https://www.usa.gov/government-works",
          "creator": { "@type": "Organization", "name": "HHS Office of Inspector General (OIG)" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
            { "@type": "ListItem", "position": 2, "name": "OIG Exclusion Lookup" }
          ]
        })}</script>
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="relative bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-[0.04]" style={{
            backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "32px 32px",
          }} />
          <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-red-500/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-3">
                <ShieldWarning className="w-4 h-4 text-red-300" weight="fill" />
                <span className="text-red-200 text-sm font-medium">Federal Sanctions Check</span>
              </div>
              <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-white mb-2" data-testid="oig-title">
                OIG Exclusion Lookup
              </h1>
              <p className="text-slate-300 text-base max-w-xl mx-auto">
                Check if a provider is on the HHS Office of Inspector General's
                List of Excluded Individuals/Entities (LEIE).
              </p>
              {stats?.loaded && (
                <p className="text-slate-400 text-xs mt-2">
                  Database: {stats.total_records?.toLocaleString()} exclusion records
                </p>
              )}
            </div>

            {/* Search Form */}
            <div className="max-w-xl mx-auto">
              <div className="bg-white rounded-2xl p-2 shadow-2xl shadow-black/20">
                {/* Mode Toggle */}
                <div className="flex gap-1 mb-2 bg-slate-100 rounded-xl p-1">
                  <button
                    onClick={() => { setMode("npi"); setError(null); }}
                    className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
                      mode === "npi" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="oig-tab-npi"
                  >
                    Search by NPI
                  </button>
                  <button
                    onClick={() => { setMode("name"); setError(null); }}
                    className={`flex-1 text-sm font-medium py-2 rounded-lg transition-colors ${
                      mode === "name" ? "bg-white text-navy shadow-sm" : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid="oig-tab-name"
                  >
                    Search by Name
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  {mode === "npi" ? (
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <MagnifyingGlass className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="Enter 10-digit NPI number"
                          value={npiInput}
                          onChange={(e) => setNpiInput(e.target.value.replace(/\D/g, "").slice(0, 10))}
                          className="h-12 pl-11 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl font-mono"
                          data-testid="oig-npi-input"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading || npiInput.length !== 10}
                        className="h-12 px-6 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl shrink-0"
                        data-testid="oig-search-btn"
                      >
                        {loading ? <CircleNotch className="w-5 h-5 animate-spin" /> : "Check"}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="First name"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="oig-first-name"
                        />
                        <Input
                          type="text"
                          placeholder="Last name"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="oig-last-name"
                        />
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="text"
                          placeholder="Business name (optional)"
                          value={businessName}
                          onChange={(e) => setBusinessName(e.target.value)}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl flex-1"
                          data-testid="oig-business-name"
                        />
                        <Input
                          type="text"
                          placeholder="State (e.g. NY)"
                          value={stateFilter}
                          onChange={(e) => setStateFilter(e.target.value.toUpperCase().slice(0, 2))}
                          className="h-12 text-base border-0 shadow-none focus-visible:ring-0 bg-slate-50 rounded-xl w-24"
                          data-testid="oig-state-filter"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={loading || (!lastName.trim() && !businessName.trim())}
                        className="w-full h-12 bg-teal hover:bg-teal/90 text-white font-semibold rounded-xl"
                        data-testid="oig-name-search-btn"
                      >
                        {loading ? <CircleNotch className="w-5 h-5 animate-spin mr-2" /> : null}
                        Search Exclusion List
                      </Button>
                    </div>
                  )}
                </form>
              </div>
              {error && <p className="text-red-400 text-sm text-center mt-2" data-testid="oig-error">{error}</p>}
            </div>
          </div>
        </section>

        {/* Breadcrumbs */}
        {!isLanding && (
          <nav className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-sm" data-testid="oig-breadcrumbs">
            <ol className="flex items-center gap-2 text-slate-500 flex-wrap">
              <li><Link to="/" className="hover:text-teal">Home</Link></li>
              <li>/</li>
              <li><Link to="/oig-exclusion-lookup" className="hover:text-teal">OIG Exclusion Lookup</Link></li>
              {npiParam && (
                <>
                  <li>/</li>
                  <li className="text-slate-900 font-medium">NPI {npiParam}</li>
                </>
              )}
            </ol>
          </nav>
        )}

        {/* Results */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* NPI direct check - show clear/excluded card */}
          {results !== null && mode === "npi" && npiParam && (
            <div className="mb-6" data-testid="oig-npi-result">
              {results.length > 0 ? (
                <ExclusionCard record={results[0]} />
              ) : (
                <div className="bg-white rounded-xl border-2 border-emerald-200 overflow-hidden">
                  <div className="px-5 py-4 bg-emerald-50 border-b-2 border-emerald-200">
                    <div className="flex items-center gap-3">
                      <ShieldCheck className="w-8 h-8 text-emerald-600 shrink-0" weight="fill" />
                      <div>
                        <p className="font-display font-bold text-lg text-emerald-700" data-testid="oig-clear-text">
                          Not Excluded
                        </p>
                        <p className="text-sm text-emerald-600">NPI {npiParam} does not appear on the OIG exclusion list.</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 space-y-2">
                    <p className="text-sm text-slate-600">
                      This NPI was not found in the HHS OIG List of Excluded Individuals/Entities (LEIE).
                    </p>
                    <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-4">
                      <Link
                        to={`/provider/${npiParam}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
                        data-testid="oig-view-profile-link"
                      >
                        View NPI Profile <ArrowRight className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/pecos-lookup/npi/${npiParam}`}
                        className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-teal transition-colors"
                      >
                        Check PECOS Status <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                    {lastUpdated && (
                      <p className="text-xs text-slate-400 pt-2">
                        LEIE data last refreshed: {new Date(lastUpdated).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Name search results */}
          {results !== null && mode === "name" && results.length > 0 && (
            <div data-testid="oig-name-results">
              <h2 className="font-display font-semibold text-lg text-slate-900 mb-1">
                Exclusion Records Found
              </h2>
              <p className="text-sm text-slate-500 mb-4">{totalCount.toLocaleString()} record{totalCount !== 1 ? "s" : ""} match your search.</p>
              <div className="space-y-3">
                {results.map((rec, i) => (
                  <ExclusionCard key={i} record={rec} compact />
                ))}
              </div>

              {/* Pagination */}
              {(hasMore || page > 1) && (
                <div className="flex items-center justify-center gap-3 mt-6" data-testid="oig-pagination">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                  >
                    <CaretLeft className="w-4 h-4 mr-1" /> Previous
                  </Button>
                  <span className="text-sm text-slate-500 font-medium px-3">Page {page}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    disabled={!hasMore}
                  >
                    Next <CaretRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* No results for name search */}
          {results !== null && mode === "name" && results.length === 0 && !loading && (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center" data-testid="oig-no-results">
              <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto mb-3" weight="fill" />
              <h3 className="font-display font-semibold text-lg text-slate-900 mb-1">No Exclusions Found</h3>
              <p className="text-slate-500 text-sm">
                No records matching your search were found in the OIG exclusion database.
              </p>
            </div>
          )}
        </section>

        {/* Explainer Section */}
        <section className="bg-slate-50 py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="oig-explainer">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center mb-3">
                  <ShieldWarning className="w-5 h-5 text-red-600" weight="fill" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">What is the OIG LEIE?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  The List of Excluded Individuals/Entities (LEIE) is maintained by the HHS Office of Inspector General.
                  It lists providers banned from participating in federal healthcare programs like Medicare and Medicaid.
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-amber-500/10 flex items-center justify-center mb-3">
                  <Warning className="w-5 h-5 text-amber-600" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">Why check exclusions?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Healthcare organizations are required to screen all employees and contractors against the LEIE.
                  Employing or contracting with an excluded individual can result in Civil Monetary Penalties (CMPs).
                </p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="w-9 h-9 rounded-lg bg-navy/10 flex items-center justify-center mb-3">
                  <Info className="w-5 h-5 text-navy" weight="fill" />
                </div>
                <h3 className="font-display font-semibold text-sm text-slate-900 mb-2">How current is the data?</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Our database mirrors the official HHS OIG LEIE, which is updated monthly. Reinstated individuals are
                  automatically removed from the list. We refresh our data monthly to stay current.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h2 className="font-display font-semibold text-xl text-slate-900 mb-6 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-3" data-testid="oig-faq">
            {[
              { q: "What happens if a provider is on the LEIE?", a: "An excluded provider cannot participate in any federal healthcare program. This means no Medicare, Medicaid, TRICARE, or other federal program payments can be made for items or services they provide. Organizations employing excluded individuals face Civil Monetary Penalties (CMPs) of up to $100,000 per item/service." },
              { q: "How often should I check the LEIE?", a: "The OIG recommends monthly screening of all employees, contractors, and vendors against the LEIE. Many organizations perform checks at hiring and then monthly thereafter. Some states require more frequent screening." },
              { q: "What's the difference between mandatory and permissive exclusions?", a: "Mandatory exclusions (Section 1128(a)) are required by law for serious offenses like program-related fraud convictions. Permissive exclusions (Section 1128(b)) are at the OIG's discretion for lesser offenses like license revocation. The minimum exclusion period for mandatory exclusions is 5 years." },
              { q: "Can an excluded provider be reinstated?", a: "Yes. After the minimum exclusion period, a provider can apply for reinstatement. The OIG reviews each case individually. Reinstated providers are removed from the LEIE database." },
            ].map((faq, i) => (
              <details key={i} className="group bg-white border border-slate-200 rounded-lg">
                <summary className="flex items-center justify-between p-4 cursor-pointer select-none hover:bg-slate-50/50 transition-colors rounded-lg">
                  <span className="font-medium text-slate-900 text-sm pr-4">{faq.q}</span>
                  <ArrowRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform shrink-0" />
                </summary>
                <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{faq.a}</div>
              </details>
            ))}
          </div>
        </section>
      </div>
    </>
  );
};

// ---- Exclusion Card Component ----
const ExclusionCard = ({ record, compact = false }) => {
  const isIndividual = !!(record.lastname || record.firstname);
  const name = isIndividual
    ? `${record.firstname || ""} ${record.midname || ""} ${record.lastname || ""}`.replace(/\s+/g, " ").trim()
    : record.busname || "Unknown";

  return (
    <div className={`bg-white rounded-xl border-2 border-red-200 overflow-hidden ${compact ? "" : ""}`}>
      <div className="px-5 py-3 bg-red-50 border-b border-red-200">
        <div className="flex items-center gap-3">
          <ShieldWarning className="w-6 h-6 text-red-600 shrink-0" weight="fill" />
          <div className="flex-1 min-w-0">
            <p className={`font-display font-bold ${compact ? "text-base" : "text-lg"} text-red-700 truncate`} data-testid="oig-excluded-name">
              {name}
            </p>
            {!compact && (
              <p className="text-sm text-red-600 font-medium">Excluded from Federal Healthcare Programs</p>
            )}
          </div>
          {isIndividual ? (
            <UserCircle className="w-5 h-5 text-red-400 shrink-0" />
          ) : (
            <Buildings className="w-5 h-5 text-red-400 shrink-0" />
          )}
        </div>
      </div>
      <div className={`${compact ? "px-4 py-3" : "p-5"} space-y-2`}>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
          {record.npi && (
            <div>
              <span className="text-slate-500 text-xs">NPI</span>
              <p className="font-mono font-medium text-slate-900">{record.npi}</p>
            </div>
          )}
          {record.excldate && (
            <div>
              <span className="text-slate-500 text-xs">Exclusion Date</span>
              <p className="font-medium text-slate-900">{record.excldate}</p>
            </div>
          )}
          {record.excltype_desc && (
            <div className="col-span-2">
              <span className="text-slate-500 text-xs">Reason</span>
              <p className="font-medium text-red-700">{record.excltype_desc}</p>
            </div>
          )}
          {record.general && (
            <div>
              <span className="text-slate-500 text-xs">Type</span>
              <p className="font-medium text-slate-900">{record.general}</p>
            </div>
          )}
          {record.specialty && (
            <div>
              <span className="text-slate-500 text-xs">Specialty</span>
              <p className="font-medium text-slate-900">{record.specialty}</p>
            </div>
          )}
          {(record.city || record.state) && (
            <div>
              <span className="text-slate-500 text-xs">Location</span>
              <p className="font-medium text-slate-900">{[record.city, record.state].filter(Boolean).join(", ")}</p>
            </div>
          )}
        </div>
        {record.npi && !compact && (
          <div className="pt-2 border-t border-slate-100 flex flex-wrap gap-4">
            <Link
              to={`/provider/${record.npi}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-teal hover:text-teal/80 transition-colors"
            >
              View NPI Profile <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={`/pecos-lookup/npi/${record.npi}`}
              className="inline-flex items-center gap-2 text-sm font-medium text-navy hover:text-teal transition-colors"
            >
              Check PECOS Status <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default OigExclusionLookup;
